import { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Shield } from 'react-feather';
import { TaxExemptionTeamMember, TaxExemptionTeamMemberCreate } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import {
  getSupplierTaxExemptionTeam,
  createTaxExemptionTeamMember,
  updateTaxExemptionTeamMember,
  deleteTaxExemptionTeamMember,
} from '../../../../../redux/services/supplierAdditionalDetailsService';
import { DillButton } from '../../../../general/Dill';
import { TaxExemptionTeamFormModal } from './TaxExemptionTeamFormModal';

interface TaxExemptionTeamSettingsSectionProps {
  supplierId: string;
}

export const TaxExemptionTeamSettingsSection = ({
  supplierId,
}: TaxExemptionTeamSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const { taxExemptionTeam, loadingList } = useAppSelector((state) => state.taxExemptionTeam);
  const { supplierUsers } = useAppSelector((state) => state.supplier);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingTeamMember, setEditingTeamMember] = useState<TaxExemptionTeamMember | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (supplierId) {
      dispatch(getSupplierTaxExemptionTeam({ supplierId }));
    }
  }, [supplierId, dispatch]);

  const isLoading = loadingList.includes('getSupplierTaxExemptionTeam');

  const handleCreate = () => {
    setEditingTeamMember(null);
    setErrorMessage(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (teamMember: TaxExemptionTeamMember) => {
    setEditingTeamMember(teamMember);
    setErrorMessage(null);
    setIsFormModalOpen(true);
  };

  const handleClose = () => {
    setIsFormModalOpen(false);
    setEditingTeamMember(null);
    setErrorMessage(null);
  };

  const extractErrorMessage = (payload: unknown): string | null => {
    if (payload && typeof payload === 'object' && 'errors' in (payload as any)) {
      const errors = (payload as any).errors;
      if (Array.isArray(errors) && errors[0]?.msg) return errors[0].msg as string;
    }
    return null;
  };

  const handleSubmit = async (data: TaxExemptionTeamMemberCreate) => {
    if (!supplierId) return;
    setErrorMessage(null);
    const result = editingTeamMember
      ? await dispatch(
          updateTaxExemptionTeamMember({
            supplierId,
            teamMemberId: editingTeamMember.id,
            data,
          })
        )
      : await dispatch(createTaxExemptionTeamMember({ supplierId, data }));
    const payload = result.payload as { success?: boolean } | null;
    if (!payload || payload.success === false) {
      setErrorMessage(extractErrorMessage(payload) || 'Failed to save team member.');
      return;
    }
    handleClose();
  };

  const handleDelete = async (teamMemberId: string) => {
    await dispatch(deleteTaxExemptionTeamMember({ supplierId, teamMemberId }));
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4 gap-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Shield size={18} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 m-0">Tax Exemption Team</p>
            <p className="text-xs text-gray-400 mt-0.5 m-0">
              Add the people who handle tax exemption disputes, then pick "Tax Exemption Team" as a
              Dispute Routing Rule's assignee for the Tax Exemption category.
            </p>
          </div>
        </div>
        <DillButton
          label="New team member"
          icon={<Plus size={14} />}
          onClick={handleCreate}
          height={31}
        />
      </div>

      {isLoading && taxExemptionTeam.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-8">Loading tax exemption team…</p>
      )}

      {!isLoading && taxExemptionTeam.length === 0 && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl py-10 flex flex-col items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-300 flex items-center justify-center">
            <Shield size={18} />
          </div>
          <p className="text-sm text-gray-500 m-0 mt-1">No team members yet</p>
          <p className="text-xs text-gray-400 m-0">
            Add your team here, then assign them tax exemption disputes.
          </p>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {taxExemptionTeam.map((teamMember) => (
          <div
            key={teamMember.id}
            className="group bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-blue-50 text-blue-600">
              <Shield size={18} />
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-sm font-semibold text-gray-900 m-0 truncate">
                  {teamMember.name}
                </p>
                {teamMember.userId && (
                  <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded-full px-2 py-0.5 shrink-0">
                    Dill User
                  </span>
                )}
                {!teamMember.userId && (
                  <span className="text-[10px] font-medium text-gray-700 bg-gray-50 rounded-full px-2 py-0.5 shrink-0">
                    No Dill Access
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 m-0 truncate">
                {[teamMember.email, teamMember.phone].filter(Boolean).join(' · ') ||
                  'No contact info'}
              </p>
            </div>

            <div className="flex items-center gap-2 shrink-0 pl-3 border-l border-gray-100">
              <DillButton
                variant="icon-default"
                icon={<Edit2 size={12} />}
                onClick={() => handleEdit(teamMember)}
              />
              <DillButton
                variant="icon-red"
                icon={<Trash2 size={12} />}
                onClick={() => handleDelete(teamMember.id)}
              />
            </div>
          </div>
        ))}
      </div>

      <TaxExemptionTeamFormModal
        open={isFormModalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        initialData={editingTeamMember}
        isLoading={
          loadingList.includes('createTaxExemptionTeamMember') ||
          loadingList.includes('updateTaxExemptionTeamMember')
        }
        errorMessage={errorMessage}
        supplierUsers={supplierUsers}
        existingTeamMembers={taxExemptionTeam}
      />
    </div>
  );
};

export default TaxExemptionTeamSettingsSection;
